import { createContext, useState, useEffect } from "react";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import { useHistory } from "react-router-use-history";

const ApiContext = createContext();

// Custom JWT Decoder
const decodeJWT = (token) => {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
				.join("")
		);
		return JSON.parse(jsonPayload);
	} catch (e) {
		return null;
	}
};

// Check if token is expired
const isTokenValid = (decodedToken) => {
	if (!decodedToken) return false;
	const currentTime = Math.floor(Date.now() / 1000);
	return decodedToken.exp > currentTime;
};

const ApiProvider = ({ children }) => {
	const [userData, setUserData] = useState(null);
	const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
	const [products, setProducts] = useState([]);
	const [pendingOrders, setPendingOrders] = useState([]);
	const [cartItems, setCartItems] = useState([]);
	const [orderData, setOrderData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [totalPrice, setTotalPrice] = useState(0);
	const [totalAmount, setTotalAmount] = useState(0);
	const [discounts, setDiscounts] = useState(0);
	const [deliveryFee, setDeliveryFee] = useState(0);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const history = useHistory();

	const autoHideDuration = 3000;

	// Function to compute total display value (including delivery fee)
	const calculateTotalDisplay = () => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "KES",
		}).format(totalPrice + deliveryFee);
	};

	// Computed totalDisplay value that updates with changes in totalPrice and deliveryFee
	const totalDisplay = calculateTotalDisplay();

	// Function to fetch updated user data
	const fetchUserData = async () => {
		try {
			const token = localStorage.getItem("token");

			if (token) {
				const response = await axios.get(
					"https://coffee-psi-three.vercel.app/users/profile",
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);
				setUserData(response.data);
			}
		} catch (error) {
			console.error("Error fetching user data:", error);
			handleLogout();
		}
	};

	useEffect(() => {
		const token = localStorage.getItem("token");
		if (token) {
			const decodedUserData = decodeJWT(token);
			if (isTokenValid(decodedUserData)) {
				setUserData(decodedUserData);
				fetchUserData();
			} else {
				handleLogout();
			}
		}
		setIsUserDataLoaded(true);
	}, []);

	const showSnackbar = (message) => {
		setSnackbarMessage(message);
		setOpenSnackbar(true);
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === "clickaway") return;
		setOpenSnackbar(false);
	};

	const handleLogin = (token) => {
		localStorage.setItem("token", token);
		const decodedUserData = decodeJWT(token);
		setUserData(decodedUserData);
		fetchUserData();
		showSnackbar("Login successful!");
	};

	const fetchProducts = async () => {
		setIsLoading(true);
		try {
			const response = await axios.get(
				"https://coffee-psi-three.vercel.app/products"
			);
			const filteredProducts = response.data.filter(
				(product) => product.productStatus !== "Draft"
			);
			setProducts(filteredProducts);
		} catch (err) {
			console.error("Error fetching products:", err);
			showSnackbar("Error fetching products.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchProducts();
	}, []);

	const fetchCartItems = async () => {
		const token = localStorage.getItem("token");
		if (!token) return;

		try {
			const response = await axios.get(
				"https://coffee-psi-three.vercel.app/cart",
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			const items = response.data.items;

			// Ensure each item has a 'discount' field
			const updatedItems = items.map((item) => ({
				...item,
				discount: item.discount || 0, // Ensure a discount field is available
			}));

			setCartItems(updatedItems);
			calculateCartTotals(updatedItems);
		} catch (err) {
			console.error("Error fetching cart items:", err);
			showSnackbar("Error fetching cart items.");
		}
	};

	useEffect(() => {
		fetchCartItems();
	}, []);

	// Recalculate totals whenever the cart items change
	const calculateCartTotals = (items) => {
		let total = 0;
		let grossAmount = 0;
		let discountAmount = 0;

		// Loop through the cart items and calculate the totals
		items.forEach((item) => {
			grossAmount += item.price * item.quantity; // Gross amount before any discounts
			total += item.totalPrice; // Total after discounts are applied to individual items
		});

		discountAmount = grossAmount - total; // Calculate total discount

		setTotalPrice(total); // Set the total price of all items after discounts
		setTotalAmount(grossAmount); // Set the gross amount before discounts
		setDiscounts(discountAmount); // Set the total discount amount
	};

	// Handle updating item quantities in the cart
	const handleQuantityChange = async (uniqueId, newQuantity) => {
		if (newQuantity < 1) return; // Avoid negative quantities
		const previousCartItems = [...cartItems];

		try {
			const token = localStorage.getItem("token");

			// Send request to update quantity on the backend
			const response = await axios.patch(
				`https://coffee-psi-three.vercel.app/cart/${uniqueId}`,
				{ quantity: newQuantity },
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
				}
			);

			if (response.status === 200) {
				// Update cart item in local state after successful quantity change
				const updatedItems = previousCartItems.map((item) =>
					item.uniqueId === uniqueId
						? {
								...item,
								quantity: newQuantity,
								// Recalculate totalPrice: apply discount if any
								totalPrice:
									item.price * newQuantity * (1 - item.discount / 100), // Apply discount to the new total
						  }
						: item
				);

				// Update the cart items and recalculate totals
				setCartItems(updatedItems);
				calculateCartTotals(updatedItems); // Recalculate the totals after quantity update
				showSnackbar("Quantity updated successfully!");
			} else {
				throw new Error("Failed to update quantity.");
			}
		} catch (error) {
			setCartItems(previousCartItems); // Revert to previous state in case of error
			showSnackbar("Error updating quantity.");
		}
	};

	// Handle removing an item from the cart
	const handleRemoveItem = async (uniqueId) => {
		const previousCartItems = [...cartItems];

		try {
			const token = localStorage.getItem("token");
			const updatedItems = previousCartItems.filter(
				(item) => item.uniqueId !== uniqueId
			);
			setCartItems(updatedItems);
			calculateCartTotals(updatedItems);

			await axios.delete(
				`https://coffee-psi-three.vercel.app/cart/${uniqueId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
				}
			);
			showSnackbar("Item removed from cart!");
		} catch (error) {
			setCartItems(previousCartItems);
			showSnackbar("Error removing item from cart.");
		}
	};

	const fetchOrders = async () => {
		const token = localStorage.getItem("token");
		if (!token) return;

		try {
			const response = await axios.get(
				"https://coffee-psi-three.vercel.app/orders",
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			setOrderData(response.data);
		} catch (err) {
			console.error("Error fetching orders:", err);
			showSnackbar("Error fetching orders.");
		}
	};

	useEffect(() => {
		fetchOrders();
	}, []);

	const handleLogout = () => {
		localStorage.removeItem("token");
		setUserData(null);
		showSnackbar("Logged out successfully!");
	};

	const handleToCart = async (e, product, selectedProduct) => {
		// Check if e (event) exists before calling stopPropagation
		if (e && e.stopPropagation) e.stopPropagation();

		if (!userData) {
			showSnackbar("Please log in to add items to cart.");
			history.push("/login");
			return;
		}

		const newCartItem = {
			uniqueId: `${product._id}-${selectedProduct.size}`,
			product,
			size: selectedProduct.size,
			quantity: 1,
			price: selectedProduct.amount,
			totalPrice: selectedProduct.amount,
		};

		setCartItems((prevItems) => {
			const updatedCartItems = [...prevItems, newCartItem];
			calculateCartTotals(updatedCartItems);
			return updatedCartItems;
		});
		showSnackbar("Item added to cart!");

		try {
			const token = localStorage.getItem("token");
			await axios.post(
				"https://coffee-psi-three.vercel.app/cart",
				[
					{
						productId: product._id,
						size: selectedProduct.size,
						quantity: 1,
					},
				],
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
		} catch (error) {
			console.error("Error adding item to cart:", error);
			showSnackbar("An error occurred while adding to cart.");
		}
	};

	return (
		<ApiContext.Provider
			value={{
				userData,
				isUserDataLoaded,
				products,
				pendingOrders,
				setOrderData,
				cartItems,
				setCartItems,
				orderData: orderData.orders || [],
				handleLogin,
				fetchCartItems,
				handleToCart,
				handleQuantityChange,
				handleRemoveItem,
				fetchOrders,
				handleLogout,
				isLoading,
				totalDisplay,
				deliveryFee,
				setDeliveryFee,
				totalPrice,
				totalAmount,
				discounts,
				updateUserProfile: fetchUserData,
			}}
		>
			{children}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={autoHideDuration}
				onClose={handleCloseSnackbar}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity="success"
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</ApiContext.Provider>
	);
};

export { ApiProvider, ApiContext };
